import { takeEvery, put, call } from 'redux-saga/effects';
import { models } from 'powerbi-client';

import * as types from './marketVolumeShareActionType';
import * as actions from './marketVolumeShareActions';
import * as services from './marketVolumeShareServices';

function* fetchMarketVolumeSharePowerBIReportSaga() {
    const environment = window.configuration.ENVIRONMENT || 'dev';
    const sharedReport = window.configuration.PowerBI_Reports?.marketVolumeShare?.sharedReport;
    const environmentModelConfig = window.configuration.PowerBI_Reports?.marketVolumeShare?.environmentModelConfig?.[environment];

    if (!sharedReport || !environmentModelConfig) {
        console.error('No Power BI report configured for environment:', environment);
        yield put(actions.fetchMarketVolumeSharePowerBIReportFailure('No Power BI report configured for this environment'));
        return;
    }

    const { reportId, reportWorkspaceId, goldenModelId } = sharedReport;
    const { modelId: targetModelId, modelWorkspaceId: targetModelWorkspaceId } = environmentModelConfig;

    try {
        const response = yield call(services.getMarketVolumeSharePowerBIToken);

        const embedPayload = {
            token: response?.access_token,
            reportId,
            workspaceId: reportWorkspaceId
        }
        const embedResponse = yield call(services.getMarketVolumeShareEmbedResponse, embedPayload);
        const tokenResponse = yield call(services.getMarketVolumeShareMultiResourceTokenResponse, {
            token: response?.access_token,
            reportId,
            goldenDatasetId: goldenModelId,
            datasetId: targetModelId,
            reportWorkspaceId,
            datasetWorkspaceId: targetModelWorkspaceId
        });

        const embedConfig = {
            type: "report",
            id: reportId,
            embedUrl: embedResponse?.embedUrl,
            accessToken: tokenResponse?.token,
            tokenType: models.TokenType.Embed,
            permissions: models.Permissions.Read,
            // Dynamic binding for a thin report: same report id, different bound semantic
            // model per environment. Note the SINGULAR `datasetBinding` shape ({ datasetId }) —
            // this is a distinct property from the plural `datasetBindings` (paginated-report
            // rebinding), which is a silent no-op for a regular report. Confirmed against a
            // working reference implementation.
            datasetBinding: {
                datasetId: targetModelId
            },
            settings: {
                filterPaneEnabled: false,
                navContentPaneEnabled: true
            }
        };

        yield put(actions.fetchMarketVolumeSharePowerBIReportSuccess(embedConfig));
    } catch (error) {
        console.error('Market Volume Share PowerBI API Error:', error);
        yield put(actions.fetchMarketVolumeSharePowerBIReportFailure('Failed to fetch PowerBI report'));
    }
}

function* fetchMarketVolumeShareLastRefreshedSaga() {
    try {
        const response = yield call(services.getMarketVolumeShareLastRefreshed);
        const lastRefreshedDateTime = response?.values?.LastRefreshedDateTime;
        const nextRefreshDateTime = response?.values?.NextRefreshDateTime ?? null;

        if (response?.success !== true || response?.values?.ExceptionId || !lastRefreshedDateTime) {
            throw new Error(response?.errorMessage || 'Failed to fetch Market Volume Share refresh date');
        }

        yield put(actions.fetchMarketVolumeShareLastRefreshedSuccess(lastRefreshedDateTime, nextRefreshDateTime));
    } catch (error) {
        console.error('Market Volume Share Last Refreshed API Error:', error);
        yield put(actions.fetchMarketVolumeShareLastRefreshedFailure('Failed to fetch refresh date'));
    }
}

export default function* marketVolumeShareSaga() {
    yield takeEvery(types.FETCH_MARKET_VOLUME_SHARE_POWER_BI_REPORT_REQUEST, fetchMarketVolumeSharePowerBIReportSaga);
    yield takeEvery(types.FETCH_MARKET_VOLUME_SHARE_LAST_REFRESHED_REQUEST, fetchMarketVolumeShareLastRefreshedSaga);
}
